/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from '../../../../__generated__/zeus/types';

import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
/** Completes the saved prequalification and returns its decision. Save changed answers before completing. */
export type BuyerMortgagePrequalificationCompleteInput = {
  /** Prequalification identifier. */
  id: Types.Scalars['UUID']['input'];
};

/** Ensures a draft exists and saves the supplied answers, including when a draft already exists. */
export type BuyerMortgagePrequalificationCreateInput = {
  /** Answers to save. */
  answers: MortgagePrequalificationAnswersInput;
};

/** Saves answers to an in-progress prequalification owned by the customer. */
export type BuyerMortgagePrequalificationUpdateInput = {
  /** Answers to save. */
  answers: MortgagePrequalificationAnswersInput;
  /** Prequalification identifier. */
  id: Types.Scalars['UUID']['input'];
};

/** Address used for mortgage qualification. */
export type MortgageAddressInput = {
  /** City. */
  city: Types.Scalars['NonEmptyString']['input'];
  /** Postal code. */
  postalCode: Types.Scalars['PostalCode']['input'];
  /** State. */
  state: Types.Scalars['NonEmptyString']['input'];
  /** Street address. */
  street: Types.Scalars['NonEmptyString']['input'];
  /** Unit number, if any. */
  unitNumber?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Unit designation, if any. */
  unitType?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
};

/** Progress of the mortgage application. */
export type MortgageApplicationStatus =
  /** Application approved; rate lock is not implied. */
  | 'APPROVED'
  /** Application steps complete and rate locked. This does not mean the loan has closed. */
  | 'COMPLETE'
  /** Application does not qualify. */
  | 'DISQUALIFIED'
  /** Borrower is filling out the application. */
  | 'IN_PROGRESS'
  /** Borrower has not started the application. */
  | 'NOT_STARTED'
  /** Application needs changes before the applicant can qualify. */
  | 'PATH_TO_QUALIFY'
  /** Application submitted to underwriting. */
  | 'SUBMITTED';

/** Customer-reported credit score range. */
export type MortgageCreditScoreRange =
  /** 580 through 619. */
  | 'SCORE_580_619'
  /** 620 through 659. */
  | 'SCORE_620_659'
  /** 660 through 719. */
  | 'SCORE_660_719'
  /** 720 or higher. */
  | 'SCORE_720_PLUS'
  /** Below 580. */
  | 'SCORE_BELOW_580';

/** Customer-reported employment status. */
export type MortgageEmploymentStatus =
  /** Employed full-time. */
  | 'EMPLOYED_FULL_TIME'
  /** Other employment status. */
  | 'OTHER'
  /** Retired. */
  | 'RETIRED'
  /** Self-employed. */
  | 'SELF_EMPLOYED';

/** Processing stage of the loan. */
export type MortgageLoanStatus =
  /** A loan account has been created before review begins. */
  | 'ACCOUNT_CREATED'
  /** Loan has been retired from active processing. */
  | 'ARCHIVED'
  /** Loan cleared for closing; closing is not yet complete. */
  | 'CLEAR_TO_CLOSE'
  /** Loan closed. */
  | 'CLOSED'
  /** Underwriting approval has conditions that must be satisfied before closing. */
  | 'CONDITIONALLY_APPROVED'
  /** Loan declined. */
  | 'DECLINED'
  /** Closing documents have been issued. */
  | 'DOCS_OUT'
  /** Loan is in final review before closing preparation. */
  | 'FINAL_REVIEW'
  /** Loan funding. */
  | 'FUNDING'
  /** Loan in progress. */
  | 'IN_PROGRESS'
  /** Loan requires manual review before a preapproval decision. */
  | 'MANUAL_REVIEW'
  /** Loan not started. */
  | 'NOT_STARTED'
  /** Loan is in post-closing processing before purchase. */
  | 'POST_CLOSING'
  /** Loan is in post-purchase processing before servicing. */
  | 'POST_PURCHASED'
  /** Initial application review and preapproval stage, before loan processing. */
  | 'PRE_APPROVED'
  /** Quality review before the loan can be cleared to close. */
  | 'PRE_CLOSING_QUALITY_REVIEW'
  /** Loan file is being prepared for underwriting. */
  | 'PROCESSING'
  /** Loan has been purchased. */
  | 'PURCHASED'
  /** Loan is in servicing. */
  | 'SERVICING'
  /** Loan is being shipped after funding. */
  | 'SHIPPING'
  /** Loan suspended. */
  | 'SUSPENDED'
  /** Loan is being reviewed for an underwriting decision. */
  | 'UNDERWRITING'
  /** Borrower withdrew the loan. */
  | 'WITHDRAWN';

/** Mortgage financing program. */
export type MortgageLoanType =
  /** Conventional mortgage. */
  | 'CONVENTIONAL'
  /** Federal Housing Administration mortgage. */
  | 'FHA'
  /** US Department of Agriculture mortgage. */
  | 'USDA'
  /** Department of Veterans Affairs mortgage. */
  | 'VA';

/** Planned use of the property. */
export type MortgageOccupancyType =
  /** Investment property. */
  | 'INVESTMENT'
  /** Primary residence. */
  | 'PRIMARY_RESIDENCE'
  /** Second home. */
  | 'SECOND_HOME';

/** Answers to save. Omitted and null answers leave stored values unchanged. */
export type MortgagePrequalificationAnswersInput = {
  /** Annual income in cents. */
  annualIncomeCents?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Total assets in cents. */
  assetsTotalCents?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Reported credit score range. */
  creditScoreRange?: MortgageCreditScoreRange | null | undefined;
  /** Current residence. */
  currentAddress?: MortgageAddressInput | null | undefined;
  /** Down payment percentage: 3, 5, 10, or 20. */
  downPaymentPercent?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Reported employment status. */
  employmentStatus?: MortgageEmploymentStatus | null | undefined;
  /** Target purchase city. */
  locationCity?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Target purchase state. */
  locationState?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Target purchase postal code. */
  locationZip?: Types.Scalars['PostalCode']['input'] | null | undefined;
  /** Monthly debt payments in cents. */
  monthlyDebtPaymentsCents?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Planned use of the property. */
  occupancyType?: MortgageOccupancyType | null | undefined;
  /** Customer contact number without an extension. */
  phoneNumber?: Types.Scalars['PhoneNumber']['input'] | null | undefined;
  /** Purchase price in cents. */
  purchasePriceCents?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** When the customer plans to buy. */
  purchaseTimeline?: MortgagePurchaseTimeline | null | undefined;
  /** Property considered for the mortgage. */
  subjectPropertyAddress?: MortgageAddressInput | null | undefined;
};

/** Progress and decision of a prequalification. */
export type MortgagePrequalificationStatus =
  /** Prequalification approved. */
  | 'APPROVED'
  /** Application completed; decision pending. */
  | 'COMPLETE'
  /** Prequalification declined. */
  | 'DECLINED'
  /** Answers in progress. */
  | 'IN_PROGRESS';

/** When the customer plans to buy. */
export type MortgagePurchaseTimeline =
  /** Buying in one to six months. */
  | 'BUY_1_TO_6_MONTHS'
  /** Buying in more than six months. */
  | 'BUY_LONGER_THAN_6_MONTHS'
  /** Ready to buy. */
  | 'BUY_NOW';

/** Audience responsible for a mortgage task. */
export type MortgageTaskCategory =
  /** Task assigned to a borrower. */
  | 'BORROWER_TASK'
  /** Document task assigned to a loan officer. */
  | 'LOAN_OFFICER_DOCUMENT_TASK';

/** Progress of a mortgage task. */
export type MortgageTaskStatus =
  /** Task cancelled automatically. */
  | 'AUTOMATICALLY_CANCELLED'
  /** Task complete. */
  | 'COMPLETED'
  /** Task in progress. */
  | 'IN_PROGRESS'
  /** Task cancelled manually. */
  | 'MANUALLY_CANCELLED'
  /** Task not started. */
  | 'NOT_STARTED'
  /** Customer submitted the task for review. */
  | 'SUBMITTED';

/** Work required by a mortgage task. */
export type MortgageTaskType =
  /** Verify data. */
  | 'DATA_VERIFICATION'
  /** Sign a disclosure. */
  | 'DISCLOSURE_SIGNING'
  /** Resolve document discrepancies. */
  | 'DOCUMENT_DISCREPANCIES'
  /** Process a document. */
  | 'DOCUMENT_PROCESSING'
  /** Provide a document. */
  | 'DOCUMENT_REQUIRED'
  /** Review income. */
  | 'INCOME_REVIEW'
  /** Provide information. */
  | 'INPUT_REQUIRED'
  /** Lock a rate. */
  | 'RATE_LOCK'
  /** Review a scenario change. */
  | 'SCENARIO_CHANGE'
  /** Order a service. */
  | 'SERVICE_ORDER'
  /** Follow instructions. */
  | 'SIMPLE_INSTRUCTIONS';

export type MortgageActiveLoanQueryVariables = Exact<{ [key: string]: never; }>;


export type MortgageActiveLoanQuery = { customerAccount: { buyer: { mortgage: { activeLoan: { id: Types.Scalars['ID']['output'], loanStatus: Types.MortgageLoanStatus | null, applicationStatus: Types.MortgageApplicationStatus, loanType: Types.MortgageLoanType | null, loanTermYears: Types.Scalars['Float']['output'] | null, purchasePriceCents: Types.Scalars['SafeInt']['output'] | null, loanAmountCents: Types.Scalars['SafeInt']['output'] | null, borrowers: Array<{ tasks: Array<{ status: Types.MortgageTaskStatus, taskType: Types.MortgageTaskType, taskCategory: Types.MortgageTaskCategory, title: Types.Scalars['String']['output'], requiredFor1003Form: Types.Scalars['Boolean']['output'] | null }> }>, latestPreapproval: { maxPurchasePriceCents: Types.Scalars['SafeInt']['output'] | null } | null, selectedPricingRate: { rate: Types.Scalars['Float']['output'] | null, loanTermYears: Types.Scalars['Float']['output'] | null } | null } | null } } } };

export type MortgageCurrentPrequalificationQueryVariables = Exact<{
  propertyAddressId?: Types.Scalars['UUID']['input'] | null | undefined;
}>;


export type MortgageCurrentPrequalificationQuery = { customerAccount: { buyer: { mortgage: { currentPrequalification: { id: Types.Scalars['ID']['output'], status: Types.MortgagePrequalificationStatus, locationCity: Types.Scalars['String']['output'] | null, locationState: Types.Scalars['String']['output'] | null, locationZip: Types.Scalars['String']['output'] | null, purchasePriceCents: Types.Scalars['SafeInt']['output'] | null, downPaymentPercent: Types.Scalars['SafeInt']['output'] | null, annualIncomeCents: Types.Scalars['SafeInt']['output'] | null, monthlyDebtPaymentsCents: Types.Scalars['SafeInt']['output'] | null, assetsTotalCents: Types.Scalars['SafeInt']['output'] | null, creditScoreRange: Types.MortgageCreditScoreRange | null, phoneNumber: Types.Scalars['String']['output'] | null, occupancyType: Types.MortgageOccupancyType | null, purchaseTimeline: Types.MortgagePurchaseTimeline | null, employmentStatus: Types.MortgageEmploymentStatus | null, currentAddress: { street: Types.Scalars['String']['output'] | null, city: Types.Scalars['String']['output'] | null, state: Types.Scalars['String']['output'] | null, postalCode: Types.Scalars['String']['output'] | null, unitNumber: Types.Scalars['String']['output'] | null } | null } | null } } } };

export type MortgagePropertyQualificationQueryVariables = Exact<{
  address: Types.MortgageAddressInput;
}>;


export type MortgagePropertyQualificationQuery = { customerAccount: { buyer: { mortgage: { propertyQualification: { disqualified: Types.Scalars['Boolean']['output'] | null } } } } };

export type BuyerMortgagePrequalificationCreateMutationVariables = Exact<{
  input: Types.BuyerMortgagePrequalificationCreateInput;
}>;


export type BuyerMortgagePrequalificationCreateMutation = { buyerMortgagePrequalificationCreate:
    | { __typename: 'InvalidInputError', message: Types.Scalars['String']['output'] }
    | { __typename: 'MortgagePrequalificationReference', id: Types.Scalars['ID']['output'] }
    | { __typename: 'NotFoundError', message: Types.Scalars['String']['output'] }
    | { __typename: 'UnauthorizedError', message: Types.Scalars['String']['output'] }
   };

export type BuyerMortgagePrequalificationUpdateMutationVariables = Exact<{
  input: Types.BuyerMortgagePrequalificationUpdateInput;
}>;


export type BuyerMortgagePrequalificationUpdateMutation = { buyerMortgagePrequalificationUpdate:
    | { __typename: 'InvalidInputError', message: Types.Scalars['String']['output'] }
    | { __typename: 'MortgagePrequalificationReference', id: Types.Scalars['ID']['output'] }
    | { __typename: 'NotFoundError', message: Types.Scalars['String']['output'] }
    | { __typename: 'UnauthorizedError', message: Types.Scalars['String']['output'] }
   };

export type BuyerMortgagePrequalificationCompleteMutationVariables = Exact<{
  input: Types.BuyerMortgagePrequalificationCompleteInput;
}>;


export type BuyerMortgagePrequalificationCompleteMutation = { buyerMortgagePrequalificationComplete:
    | { __typename: 'InvalidInputError', message: Types.Scalars['String']['output'] }
    | { __typename: 'MortgagePrequalificationCompletion', disqualified: Types.Scalars['Boolean']['output'] }
    | { __typename: 'NotFoundError', message: Types.Scalars['String']['output'] }
    | { __typename: 'UnauthorizedError', message: Types.Scalars['String']['output'] }
   };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}

export const MortgageActiveLoanDocument = /*#__PURE__*/ new TypedDocumentString(`
    query MortgageActiveLoan {
  customerAccount {
    buyer {
      mortgage {
        activeLoan {
          id
          loanStatus
          applicationStatus
          loanType
          loanTermYears
          purchasePriceCents
          loanAmountCents
          borrowers {
            tasks {
              status
              taskType
              taskCategory
              title
              requiredFor1003Form
            }
          }
          latestPreapproval {
            maxPurchasePriceCents
          }
          selectedPricingRate {
            rate
            loanTermYears
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<MortgageActiveLoanQuery, MortgageActiveLoanQueryVariables>;
export const MortgageCurrentPrequalificationDocument = /*#__PURE__*/ new TypedDocumentString(`
    query MortgageCurrentPrequalification($propertyAddressId: UUID) {
  customerAccount {
    buyer {
      mortgage {
        currentPrequalification(propertyAddressId: $propertyAddressId) {
          id
          status
          locationCity
          locationState
          locationZip
          purchasePriceCents
          downPaymentPercent
          annualIncomeCents
          monthlyDebtPaymentsCents
          assetsTotalCents
          creditScoreRange
          phoneNumber
          occupancyType
          purchaseTimeline
          employmentStatus
          currentAddress {
            street
            city
            state
            postalCode
            unitNumber
          }
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<MortgageCurrentPrequalificationQuery, MortgageCurrentPrequalificationQueryVariables>;
export const MortgagePropertyQualificationDocument = /*#__PURE__*/ new TypedDocumentString(`
    query MortgagePropertyQualification($address: MortgageAddressInput!) {
  customerAccount {
    buyer {
      mortgage {
        propertyQualification(address: $address) {
          disqualified
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<MortgagePropertyQualificationQuery, MortgagePropertyQualificationQueryVariables>;
export const BuyerMortgagePrequalificationCreateDocument = /*#__PURE__*/ new TypedDocumentString(`
    mutation BuyerMortgagePrequalificationCreate($input: BuyerMortgagePrequalificationCreateInput!) {
  buyerMortgagePrequalificationCreate(input: $input) {
    __typename
    ... on MortgagePrequalificationReference {
      id
    }
    ... on Error {
      message
    }
  }
}
    `) as unknown as TypedDocumentString<BuyerMortgagePrequalificationCreateMutation, BuyerMortgagePrequalificationCreateMutationVariables>;
export const BuyerMortgagePrequalificationUpdateDocument = /*#__PURE__*/ new TypedDocumentString(`
    mutation BuyerMortgagePrequalificationUpdate($input: BuyerMortgagePrequalificationUpdateInput!) {
  buyerMortgagePrequalificationUpdate(input: $input) {
    __typename
    ... on MortgagePrequalificationReference {
      id
    }
    ... on Error {
      message
    }
  }
}
    `) as unknown as TypedDocumentString<BuyerMortgagePrequalificationUpdateMutation, BuyerMortgagePrequalificationUpdateMutationVariables>;
export const BuyerMortgagePrequalificationCompleteDocument = /*#__PURE__*/ new TypedDocumentString(`
    mutation BuyerMortgagePrequalificationComplete($input: BuyerMortgagePrequalificationCompleteInput!) {
  buyerMortgagePrequalificationComplete(input: $input) {
    __typename
    ... on MortgagePrequalificationCompletion {
      disqualified
    }
    ... on Error {
      message
    }
  }
}
    `) as unknown as TypedDocumentString<BuyerMortgagePrequalificationCompleteMutation, BuyerMortgagePrequalificationCompleteMutationVariables>;